import React, {useEffect, useRef, useState} from 'react';
import Wrapper from '../../components/Wrapper';
import {Helmet} from 'react-helmet';
import DashboardHeader from '../../components/DashboardHeader';
import './index.scss';
import CorporateDashboardCard from '../../components/Corporate/CorporateDashboardCard';
import TotalPieChart from '../../components/Charts/TotalPieChart';
import CorporatePersonalStats from '../../components/Corporate/CorporatePersonalStats';
import {
  useLazyDashboardCorporateEquipmentQuery,
  useLazyDashboardCorporateFBOQuery,
  useLazyDashboardCorporatePersonnelQuery,
} from '../../store/builder/inspections.api';
import {setLoader} from '../../store/builder/reducer';
import {useDispatch} from 'react-redux';
import CorporateUsersModal from '../../components/Corporate/CorporateUsersModal';
import AnimatedLoader from '../../components/AnimatedLoader/AnimatedLoader';
import {useTranslation} from '../../hooks/useTranslation';
import useLoader from '../../global/UseLoadingHandler';
import CustomPaginate from '../../components/PaginateComponent';
import CompactPagination from '../../components/CompactPagination';
import {useDebounce} from '../LogSheets/hooks';

const FBO_PER_PAGE = 10;

const CorporateDashboard = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [dashboardFilterStatus, setDashboardFilterStatus] = useState(false);
  const [chosenFilter, setChosenFilter] = useState('worst');
  const [fboPage, setFboPage] = useState(1);
  const [fboSearchInput, setFboSearchInput] = useState('');
  const [usersModal, setUsersModal] = useState(false);
  const [chosenUsers, setChosenUsers] = useState(null);

  const fboFilterParam =
    chosenFilter === 'worst' ? 'worst_to_best' : 'best_to_worst';
  const debouncedFboSearch = useDebounce(fboSearchInput.trim(), 500);
  const fboSearchForApi =
    debouncedFboSearch.length >= 3 ? debouncedFboSearch : undefined;

  const [
    getCorporateDashboard,
    {
      data: corporateDashboardData,
      isFetching: corporateDashboardIsFetching,
      isUninitialized: corporateDashboardFBOUninitialized,
      isSuccess: corporateDashboardFBOSuccess,
    },
  ] = useLazyDashboardCorporateFBOQuery();

  const agencyCards = Array.isArray(corporateDashboardData?.data)
    ? corporateDashboardData.data
    : Array.isArray(corporateDashboardData)
      ? corporateDashboardData
      : [];

  const corporateFboPagination = corporateDashboardData?.pagination;

  const prevFboSearchRef = useRef(fboSearchForApi);

  // Skeleton only until the first successful FBO response. Do not use isFetching
  // alone — it stays true during refetches and would keep the skeleton visible.
  const showAgencyCardsSkeleton =
    !corporateDashboardFBOSuccess &&
    (corporateDashboardIsFetching || corporateDashboardFBOUninitialized);

  const showAgencyEmptyState =
    corporateDashboardFBOSuccess && agencyCards.length === 0;
  const showAgencyEmptyStateWithSearch =
    showAgencyEmptyState && Boolean(fboSearchForApi);

  const [
    getCorporateDashboardEquipment,
    {data: corporateDashboardEquipmentData},
  ] = useLazyDashboardCorporateEquipmentQuery();

  const [
    getCorporateDashboardPersonnel,
    {data: corporateDashboardPersonnelData},
  ] = useLazyDashboardCorporatePersonnelQuery();

  useEffect(() => {
    dispatch(setLoader(true));
    Promise.all([
      getCorporateDashboardEquipment(),
      getCorporateDashboardPersonnel(),
    ])
      .then(() => {
        dispatch(setLoader(false));
      })
      .catch(() => {
        dispatch(setLoader(false));
      });
  }, []);

  useEffect(() => {
    const searchChanged = prevFboSearchRef.current !== fboSearchForApi;
    prevFboSearchRef.current = fboSearchForApi;
    const page = searchChanged ? 1 : fboPage;
    if (searchChanged && fboPage !== 1) {
      setFboPage(1);
    }
    getCorporateDashboard({
      filter: fboFilterParam,
      page,
      per_page: FBO_PER_PAGE,
      search: fboSearchForApi,
    });
  }, [
    fboFilterParam,
    fboPage,
    fboSearchForApi,
    getCorporateDashboard,
  ]);

  useLoader([corporateDashboardIsFetching]);

  const handleFboPageClick = page => {
    setFboPage(page.selected + 1);
    window.scrollTo(0, 0);
  };

  const handleFboCompactPageChange = page => {
    setFboPage(page);
    window.scrollTo(0, 0);
  };

  return (
    <Wrapper>
      <Helmet>
        <title>{t('corporateDashboard.pageTitle')}</title>
      </Helmet>
      <DashboardHeader
        title={t('corporateDashboard.title')}
        type='corporate-dashboard'
        // action={() => setCreateModal(true)}
        // secondAction={() => setInviteModal(true)}
      />
      {usersModal && (
        <CorporateUsersModal
          exit={() => {
            setUsersModal(false);
            setChosenUsers(null);
          }}
          chosenUsers={chosenUsers}
        />
      )}
      <div className='corporate-dashboard'>
        <div className='corporate-dashboard__block'>
          {corporateDashboardEquipmentData ? (
            <TotalPieChart
              data={{
                data: corporateDashboardEquipmentData?.chart,
                nfc_enabled:
                  corporateDashboardEquipmentData?.nfc_activated_percentage,
              }}
            />
          ) : (
            <div className='corporate__skeleton'></div>
          )}
          {corporateDashboardPersonnelData ? (
            <CorporatePersonalStats
              data={corporateDashboardPersonnelData}
              setUsersModal={setUsersModal}
              setChosenUsers={setChosenUsers}
            />
          ) : (
            <div className='corporate__skeleton'></div>
          )}
        </div>
        <div className='corporate-dashboard__toolbar'>
          <div
            className={`corporate-dashboard__filter ${dashboardFilterStatus ? 'active' : ''}`}
            onClick={() => setDashboardFilterStatus(!dashboardFilterStatus)}
          >
            <p>
              {chosenFilter === 'worst'
                ? t('corporateDashboard.filters.fromWorstToBest')
                : t('corporateDashboard.filters.fromBestToWorst')}
            </p>
            <img src='/assets/icons/chevron-down.svg' alt='arrow' />
            {dashboardFilterStatus && (
              <div className='corporate-dashboard__dropdown'>
                <div
                  className={`corporate-dashboard__dropdown-elem ${chosenFilter === 'worst' ? 'active' : ''}`}
                  onClick={() => {
                    setChosenFilter('worst');
                    setFboPage(1);
                  }}
                >
                  <p>{t('corporateDashboard.filters.fromWorstToBest')}</p>
                  {chosenFilter === 'worst' ? (
                    <img
                      src='/assets/icons/corporate-checkmark.svg'
                      alt='check'
                    />
                  ) : null}
                </div>
                <div
                  className={`corporate-dashboard__dropdown-elem ${chosenFilter !== 'worst' ? 'active' : ''}`}
                  onClick={() => {
                    setChosenFilter('best');
                    setFboPage(1);
                  }}
                >
                  <p>{t('corporateDashboard.filters.fromBestToWorst')}</p>
                  {chosenFilter !== 'worst' ? (
                    <img
                      src='/assets/icons/corporate-checkmark.svg'
                      alt='check'
                    />
                  ) : null}
                </div>
              </div>
            )}
          </div>
          <div className='corporate-dashboard__search-block'>
            <div className='corporate-dashboard__search'>
              <input
                type='search'
                className='corporate-dashboard__search-input'
                value={fboSearchInput}
                onChange={e => setFboSearchInput(e.target.value)}
                placeholder={t('equipmentType.searchPlaceholder')}
                aria-label={t('equipmentType.searchPlaceholder')}
              />
            </div>
            {corporateFboPagination ? (
              <div className='corporate-dashboard__pagination'>
                <CompactPagination
                  currentPage={corporateFboPagination.current_page}
                  pageCount={corporateFboPagination.last_page}
                  onPageChange={handleFboCompactPageChange}
                />
              </div>
            ) : null}
          </div>
        </div>
        <div
          className={`corporate-dashboard__block cards${showAgencyEmptyState ? ' cards--empty' : ''}`}
        >
          {showAgencyCardsSkeleton ? (
            Array.from({length: 6}, (_, index) => (
              <div
                className='corporate__card corporate__card--skeleton'
                key={`corporate-card-skeleton-${index}`}
                aria-hidden
              >
                <div className='corporate__card-skeleton-head'>
                  <div className='corporate__card-skeleton-head-left'>
                    <span className='corporate__card-skeleton-avatar' />
                    <div className='corporate__card-skeleton-lines'>
                      <span className='corporate__card-skeleton-line corporate__card-skeleton-line--title' />
                      <span className='corporate__card-skeleton-line corporate__card-skeleton-line--subtitle' />
                    </div>
                  </div>
                  <span className='corporate__card-skeleton-line corporate__card-skeleton-line--short' />
                </div>
                <div className='corporate__card-skeleton-stats'>
                  {Array.from({length: 5}, (_, i) => (
                    <div className='corporate__card-skeleton-stat' key={i}>
                      <span className='corporate__card-skeleton-line corporate__card-skeleton-line--label' />
                      <span className='corporate__card-skeleton-line corporate__card-skeleton-line--value' />
                    </div>
                  ))}
                </div>
                <div className='corporate__card-skeleton-table'>
                  <div className='corporate__card-skeleton-table-header'>
                    <span className='corporate__card-skeleton-line corporate__card-skeleton-line--header' />
                    <span className='corporate__card-skeleton-filters' />
                  </div>
                  <div className='corporate__card-skeleton-table-body' />
                </div>
              </div>
            ))
          ) : showAgencyEmptyState ? (
            <div
              style={{
                width: '100%',
                height: '303px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  width: '300px',
                  gap: '16px',
                }}
              >
                <div className='expired__box-img'>
                  <AnimatedLoader />
                </div>
                <p
                  className='no-payment-text'
                  style={{
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: '300',
                    lineHeight: '140%',
                    color: '#27323c',
                    textAlign: 'center',
                  }}
                >
                  {showAgencyEmptyStateWithSearch
                    ? t('corporateDashboard.messages.noSearchResults')
                    : t('corporateDashboard.messages.noData')}
                </p>
              </div>
            </div>
          ) : (
            agencyCards.map((item, index) => (
              <CorporateDashboardCard
                item={item}
                key={item?.agency?.id ?? index}
                agencyId={item?.agency?.id}
                data={agencyCards}
              />
            ))
          )}
        </div>
        {corporateFboPagination && corporateFboPagination.last_page > 1 ? (
          <div className='corporate-dashboard__paginate'>
            <CustomPaginate
              pageCount={corporateFboPagination.last_page}
              handlePageClick={handleFboPageClick}
              currentPage={corporateFboPagination.current_page}
            />
          </div>
        ) : null}
      </div>
    </Wrapper>
  );
};

export default CorporateDashboard;
